import type { DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { allows, hasAny } from "@giftforward/entitlements";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ADD_MORE_TIER_MAP,
  AVAILABLE_COUNTRIES,
  BUDGET_LOCKED_NOTICE,
  BUDGET_PILLS,
} from "@/constants/campaignBuilder";
import { useCampaignBuilder } from "@/context/CampaignBuilderContext";
import { useProductDetail } from "@/context/ProductDetailContext";
import { useCollectionProducts } from "@/hooks/useCollectionProducts";
import { useCollectionsByBudget } from "@/hooks/useCollectionsByBudget";
import { useMyEntitlements } from "@/hooks/useMyEntitlements";
import type { Product } from "@/interfaces/product";
import { isBudgetLocked } from "@/utils/campaign-gating-helper";
import { hasCollectionOrderChanged } from "@/utils/collectionOrder-helper";
import { step1GridCollections } from "@/utils/giftFreeze-helper";
import {
  addedFromLowerTierIds,
  composeForCountry,
  resolveCountryFeed,
} from "@/utils/product-helper";
import {
  availableGiftCategories,
  hasAnyGiftCategory,
  shipsToCountry,
} from "@/utils/redemption-helper";
import AddMoreToolbar from "./AddMoreToolbar";
import AddMoreView from "./AddMoreView";
import BudgetChips from "./BudgetChips";
import BudgetSectionHeader from "./BudgetSectionHeader";
import CollectionPreviewProductGrid from "./CollectionPreviewProductGrid";
import CollectionViewControls from "./CollectionViewControls";
import EditToolbar from "./EditToolbar";

export default function BudgetPerRecipient() {
  const {
    selectedBudget,
    setSelectedBudget,
    selectedCountry,
    setSelectedCountry,
    savedGifts,
    setSavedGifts,
    setSavedGiftsByCountry,
    saveFeed,
    setPendingFeed,
    hydratedFeedEdits,
    hydratedAddedProducts,
    frozenCollections,
    campaignStatus,
    openModal,
  } = useCampaignBuilder();
  const { openProductDetail } = useProductDetail();
  const { ent } = useMyEntitlements();
  const canEditCollection = !!ent && hasAny(ent, "collection", "edit");
  const canShipInternationally = !!ent && allows(ent, "campaign_send", "regions", "international");
  /**
   * The tier is frozen once the campaign leaves draft (money has been committed against it).
   * `null` = CREATE mode ⇒ `false`, so a brand-new campaign behaves exactly as before.
   *
   * Scope is the BUDGET GRID ONLY. The reorder flow below (`isEditMode && canEditCollection`),
   * Edit Gifts, Add More and the upsell for an unentitled account are deliberately untouched:
   * reordering the gifts inside an already-chosen tier is still allowed on a scheduled campaign,
   * and it saves through the `feeds` payload, which the server's write-gate does not refuse.
   */
  const budgetLocked = isBudgetLocked(campaignStatus);

  const [isExpanded, setIsExpanded] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isAddMoreMode, setIsAddMoreMode] = useState(false);
  const [editGifts, setEditGifts] = useState<Product[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [activeCategories, setActiveCategories] = useState<string[]>([]);
  // Add More mode state
  const [addMoreTier, setAddMoreTier] = useState<number | null>(null);
  const [addMoreCollectionId, setAddMoreCollectionId] = useState<string | null>(null);
  const [addMoreSelectedIds, setAddMoreSelectedIds] = useState<Set<string>>(new Set());

  const { data: budgetData, isLoading: productsLoading } = useCollectionsByBudget(selectedBudget);
  const domesticIndex = budgetData?.domesticIndex ?? {};
  const collections = step1GridCollections(frozenCollections, budgetData?.collections);
  // Raw composed grid for the active country (no edits) — the "reset to default" baseline.
  const apiProducts = useMemo(
    () => (collections ? composeForCountry(collections, selectedCountry) : []),
    [collections, selectedCountry],
  );
  // The active country's grid = compose + that country's stored edits (empty ⇒ raw compose).
  const countryFeed = useMemo(
    () =>
      collections
        ? resolveCountryFeed(
            collections,
            selectedCountry,
            hydratedFeedEdits[selectedCountry],
            hydratedAddedProducts,
          )
        : [],
    [collections, selectedCountry, hydratedFeedEdits, hydratedAddedProducts],
  );

  // Init the active country's grid from its resolved feed when unset (new country / fresh load).
  useEffect(() => {
    if (countryFeed.length > 0 && savedGifts === null) setSavedGifts(countryFeed);
  }, [countryFeed, savedGifts, setSavedGifts]);

  // Eager-hydrate EVERY edited country (not just the visited one) so a save re-derives and keeps
  // all of them — an unvisited edited country would otherwise be dropped from the feeds payload.
  useEffect(() => {
    if (!collections) return;
    const countries = Object.keys(hydratedFeedEdits);
    if (countries.length === 0) return;
    setSavedGiftsByCountry((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const country of countries) {
        if (next[country]) continue;
        next[country] = resolveCountryFeed(
          collections,
          country,
          hydratedFeedEdits[country],
          hydratedAddedProducts,
        );
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [collections, hydratedFeedEdits, hydratedAddedProducts, setSavedGiftsByCountry]);

  const { data: addMoreProducts = [], isLoading: addMoreLoading } = useCollectionProducts(
    addMoreCollectionId ?? undefined,
  );

  const lowerTiers = selectedBudget !== null ? (ADD_MORE_TIER_MAP[selectedBudget] ?? []) : [];

  const displayedProducts = savedGifts ?? apiProducts;

  // Filter to products that ship to the selected country (shipsToCountry): a physical gift with an
  // empty shippableCountries (no Shopify data → international) is hidden, not treated as unrestricted;
  // charity/experience are country-agnostic. Domestic physical empties are stamped ["US"] server-side.
  const countryFilteredProducts = useMemo(
    () => displayedProducts.filter((p) => shipsToCountry(p, selectedCountry)),
    [displayedProducts, selectedCountry],
  );

  // Gifts in the grid on screen that come from a lower tier (not in this tier's own composed list).
  const addedIds = useMemo(
    () => addedFromLowerTierIds(isEditMode ? editGifts : countryFilteredProducts, apiProducts),
    [isEditMode, editGifts, countryFilteredProducts, apiProducts],
  );

  const categories = useMemo(
    () => availableGiftCategories(isEditMode ? editGifts : displayedProducts),
    [isEditMode, editGifts, displayedProducts],
  );

  const visibleEditGifts = useMemo(() => {
    if (activeCategories.length === 0) return editGifts;
    return editGifts.filter((p) => hasAnyGiftCategory(p, activeCategories));
  }, [editGifts, activeCategories]);

  const hasUnsavedChanges = useMemo(() => {
    if (!isEditMode) return false;
    return hasCollectionOrderChanged(editGifts, savedGifts ?? apiProducts);
  }, [isEditMode, editGifts, savedGifts, apiProducts]);

  // Report the unsaved grid so leaving Step 1 (this unmounts) saves it instead of losing it.
  useEffect(() => {
    setPendingFeed(hasUnsavedChanges ? editGifts : null);
  }, [hasUnsavedChanges, editGifts, setPendingFeed]);

  // Tab close / refresh mid-reorder → the browser's native "leave?" prompt. Latest-ref so the
  // listener is bound once instead of re-registered on every drag.
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  hasUnsavedChangesRef.current = hasUnsavedChanges;
  useEffect(() => {
    function onBeforeUnload(e: BeforeUnloadEvent) {
      if (!hasUnsavedChangesRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  const selectedCount = selectedIds.size;
  const allSelected =
    visibleEditGifts.length > 0 && visibleEditGifts.every((p) => selectedIds.has(p.id));

  const countryEntry = AVAILABLE_COUNTRIES.find((c) => c.code === selectedCountry);
  const countryLabel = countryEntry ? `${countryEntry.flag} ${countryEntry.name}` : selectedCountry;

  const editGiftIds = useMemo(() => new Set(editGifts.map((p) => p.id)), [editGifts]);

  function switchBudget(amount: number) {
    setSelectedBudget(amount); // context keeps each tier's saved grids; this only switches the view
    setIsEditMode(false);
    setIsAddMoreMode(false);
    setEditGifts([]);
    setSelectedIds(new Set());
    setActiveCategories([]);
    setAddMoreTier(null);
    setAddMoreCollectionId(null);
    setAddMoreSelectedIds(new Set());
  }

  function handleSelectBudget(amount: number) {
    // Guarded in the function, not just in CSS: a programmatic click (test, tour, stray ref) must
    // not be able to move the tier of a campaign that is already out the door.
    if (budgetLocked) return;
    if (selectedBudget === amount) return;
    if (hasUnsavedChanges) {
      // Save FIRST and only switch once the write LANDED: switching wipes the grid the payload was
      // built from, so discarding on a failed save would destroy the reorder with nothing to retry.
      // No onDismiss — backdrop/Escape leaves the user on this tier with the reorder intact.
      openModal({
        context: "unsavedChanges",
        onPrimary: async () => {
          if (await handleSave()) switchBudget(amount);
        },
        onSecondary: () => switchBudget(amount),
      });
      return;
    }
    switchBudget(amount);
  }

  function handleSelectCountry(country: string) {
    // Country is a per-country grid switch, NOT a reset: the target country's grid is loaded
    // (or composed on first visit) from the map; this country's edits stay put.
    setSelectedCountry(country);
  }

  function handleEnterEdit() {
    setEditGifts(countryFilteredProducts);
    setSelectedIds(new Set());
    setActiveCategories([]);
    setIsEditMode(true);
  }

  async function handleSave(): Promise<boolean> {
    // Commit the edited grid to context AND persist Step 1 (also the path the unsaved-changes
    // modal's "Save" runs, since it's wired to this same onSave).
    // Edit mode is left only once the write landed, so a failed save keeps the grid on screen.
    const saved = await saveFeed(editGifts);
    if (!saved) return false;
    setIsEditMode(false);
    setSelectedIds(new Set());
    setActiveCategories([]);
    return true;
  }

  function handleReset() {
    setEditGifts(apiProducts);
    setSelectedIds(new Set());
    setActiveCategories([]);
  }

  function handleExit() {
    setIsEditMode(false);
    setSelectedIds(new Set());
    setActiveCategories([]);
  }

  function handleSelectAll() {
    const visibleIds = visibleEditGifts.map((p) => p.id);
    const allVisibleSelected = visibleIds.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const id of visibleIds) next.delete(id);
      } else {
        for (const id of visibleIds) next.add(id);
      }
      return next;
    });
  }

  function handleDelete() {
    setEditGifts((prev) => prev.filter((p) => !selectedIds.has(p.id)));
    setSelectedIds(new Set());
  }

  function handleMoveTop() {
    setEditGifts((prev) => {
      const selected = prev.filter((p) => selectedIds.has(p.id));
      const rest = prev.filter((p) => !selectedIds.has(p.id));
      return [...selected, ...rest];
    });
    setSelectedIds(new Set());
  }

  function handleMoveBottom() {
    setEditGifts((prev) => {
      const selected = prev.filter((p) => selectedIds.has(p.id));
      const rest = prev.filter((p) => !selectedIds.has(p.id));
      return [...rest, ...selected];
    });
    setSelectedIds(new Set());
  }

  function handleMoveToPosition(position: number) {
    setEditGifts((prev) => {
      const selected = prev.filter((p) => selectedIds.has(p.id));
      const rest = prev.filter((p) => !selectedIds.has(p.id));
      const clampedPos = Math.max(1, Math.min(position, prev.length)) - 1;
      const next = [...rest];
      next.splice(clampedPos, 0, ...selected);
      return next;
    });
    setSelectedIds(new Set());
  }

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleRemoveCard = useCallback((id: string) => {
    setEditGifts((prev) => prev.filter((p) => p.id !== id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setEditGifts((prev) => {
      const oldIndex = prev.findIndex((p) => p.id === String(active.id));
      const newIndex = prev.findIndex((p) => p.id === String(over.id));
      return arrayMove(prev, oldIndex, newIndex);
    });
  }

  // Add More handlers
  function handleOpenAddMore() {
    setIsAddMoreMode(true);
    setAddMoreTier(null);
    setAddMoreCollectionId(null);
    setAddMoreSelectedIds(new Set());
  }

  function handleSelectAddMoreTier(amount: number) {
    setAddMoreTier(amount);
    setAddMoreCollectionId(domesticIndex[amount] ?? null);
    setAddMoreSelectedIds(new Set());
  }

  function handleToggleAddMoreSelect(id: string) {
    if (editGiftIds.has(id)) return;
    setAddMoreSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleConfirmAddMore() {
    const toAdd = addMoreProducts.filter(
      (p) => addMoreSelectedIds.has(p.id) && !editGiftIds.has(p.id),
    );
    setEditGifts((prev) => [...toAdd, ...prev]);
    setIsAddMoreMode(false);
    setAddMoreTier(null);
    setAddMoreCollectionId(null);
    setAddMoreSelectedIds(new Set());
  }

  function handleExitAddMore() {
    setIsAddMoreMode(false);
    setAddMoreTier(null);
    setAddMoreCollectionId(null);
    setAddMoreSelectedIds(new Set());
  }

  return (
    <>
      <div
        className="rounded-card bg-white overflow-hidden transition-shadow duration-200"
        style={{
          border: "1px solid var(--color-smoke)",
          boxShadow: isExpanded ? "0 4px 20px rgba(51,51,51,0.06)" : "none",
        }}
      >
        <BudgetSectionHeader isExpanded={isExpanded} onToggle={() => setIsExpanded((v) => !v)} />

        {isExpanded && (
          <>
            {!isAddMoreMode && (
              <>
                <div className="border-t border-smoke mx-5" />
                {/* Notice sits OUTSIDE the dimmed grid so the explanation stays fully legible. */}
                {budgetLocked && (
                  <div
                    className="flex items-center gap-2 mx-5 mt-5 px-3 py-2 rounded-lg"
                    style={{
                      background: "var(--color-light-dust)",
                      border: "1px solid var(--color-smoke)",
                    }}
                  >
                    <LockOutlinedIcon sx={{ fontSize: 14, color: "var(--color-dark-dust)" }} />
                    <p className="font-lato text-[12px] text-graphite leading-none">
                      {BUDGET_LOCKED_NOTICE}
                    </p>
                  </div>
                )}
                <BudgetChips
                  budgetOptions={[...BUDGET_PILLS]}
                  selectedBudget={selectedBudget}
                  onSelect={handleSelectBudget}
                  locked={budgetLocked}
                />
              </>
            )}

            {/* Collection preview — shown once a budget is selected */}
            {selectedBudget !== null && (
              <>
                {/* Collection preview heading — always shown */}
                <div className="flex flex-col items-center gap-1 py-4">
                  <p className="font-crimson text-[26px] leading-[1.2] text-rodeo-black">
                    ${selectedBudget} Collection Preview
                  </p>
                  <p className="font-lato text-[15px] text-graphite">Shipping and tax included</p>
                </div>

                {/* NOT gated by `budgetLocked` — see the comment on it above: reordering the
                    gifts of an already-scheduled campaign is the one Step-1 interaction that
                    stays alive, so this branch must never read the lock. */}
                {isEditMode && canEditCollection ? (
                  isAddMoreMode ? (
                    <>
                      <AddMoreToolbar
                        addCount={addMoreSelectedIds.size}
                        addMoreTier={addMoreTier}
                        onExit={handleExitAddMore}
                        onConfirm={handleConfirmAddMore}
                      />
                      <AddMoreView
                        lowerTiers={lowerTiers}
                        selectedTier={addMoreTier}
                        onSelectTier={handleSelectAddMoreTier}
                        isLoading={addMoreLoading}
                        products={addMoreProducts}
                        existingIds={editGiftIds}
                        selectedIds={addMoreSelectedIds}
                        onToggleSelect={handleToggleAddMoreSelect}
                        onProductClick={(id) => openProductDetail(id)}
                      />
                    </>
                  ) : (
                    <>
                      <EditToolbar
                        countryLabel={countryLabel}
                        selectedCount={selectedCount}
                        canAddMore={lowerTiers.length > 0 && selectedCountry === "US"}
                        onAddMore={handleOpenAddMore}
                        allSelected={allSelected}
                        onSelectAll={handleSelectAll}
                        onDelete={handleDelete}
                        onMoveTop={handleMoveTop}
                        onMoveBottom={handleMoveBottom}
                        onMoveToPosition={handleMoveToPosition}
                        onReset={handleReset}
                        onExit={handleExit}
                        onSave={handleSave}
                        hasUnsavedChanges={hasUnsavedChanges}
                        categories={categories}
                        activeCategories={activeCategories}
                        onToggleCategory={(cat) =>
                          setActiveCategories((prev) =>
                            prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat],
                          )
                        }
                        onClearCategories={() => setActiveCategories([])}
                      />
                      <CollectionPreviewProductGrid
                        isLoading={productsLoading}
                        isEditMode
                        isProUnlocked={canEditCollection}
                        displayedProducts={displayedProducts}
                        visibleEditGifts={visibleEditGifts}
                        editGifts={editGifts}
                        selectedIds={selectedIds}
                        onProductClick={(id) => openProductDetail(id)}
                        onToggleSelect={handleToggleSelect}
                        onRemoveCard={handleRemoveCard}
                        onDragEnd={handleDragEnd}
                        addedIds={addedIds}
                      />
                    </>
                  )
                ) : (
                  <>
                    {/* Available Gifts In + Edit Gift row (view mode only) */}
                    <CollectionViewControls
                      selectedCountry={selectedCountry}
                      onSelectCountry={handleSelectCountry}
                      canShipInternationally={canShipInternationally}
                      canEditCollection={canEditCollection}
                      onEnterEdit={handleEnterEdit}
                    />

                    <CollectionPreviewProductGrid
                      isLoading={productsLoading}
                      isEditMode={false}
                      isProUnlocked={canEditCollection}
                      displayedProducts={countryFilteredProducts}
                      visibleEditGifts={[]}
                      editGifts={[]}
                      selectedIds={new Set()}
                      onProductClick={(id) => openProductDetail(id)}
                      onToggleSelect={() => {}}
                      onRemoveCard={() => {}}
                      onDragEnd={() => {}}
                      addedIds={addedIds}
                    />
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}
